import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { CustomerSearchItem } from "../types";
import { useCustomerLookup } from "../services";
import Input from "@/components/common/Form/Input";
import Button from "@/components/common/Button";
import { Icon } from "@/components/Icons";

interface CustomerLookupProps {
  onSelect: (customer: CustomerSearchItem) => void;
  selectedCustomer?: CustomerSearchItem | null;
  disabled?: boolean;
  onClear?: () => void;
}

export interface CustomerLookupHandle {
  focus: () => void;
}

const useDebounce = <T,>(value: T, delay = 400) => {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
};

const CustomerLookup = forwardRef<CustomerLookupHandle, CustomerLookupProps>(
  ({ onSelect, selectedCustomer, disabled = false, onClear }, ref) => {
    const [searchText, setSearchText] = useState("");
    const [customerResults, setCustomerResults] = useState<
      CustomerSearchItem[]
    >([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const customerLookupMutation = useCustomerLookup();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const selectedItemRef = useRef<HTMLDivElement | null>(null);
    const justSelectedRef = useRef(false);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }));

    const debouncedSearch = useDebounce(searchText, 400);

    // Fetch customers when debounced search changes
    useEffect(() => {
      if (justSelectedRef.current) {
        justSelectedRef.current = false;
        return;
      }

      const term = debouncedSearch.trim();
      if (!term || term.length < 2) {
        setCustomerResults([]);
        setShowDropdown(false);
        return;
      }

      // Don't search if already selected the same customer
      if (selectedCustomer && term === selectedCustomer.name) {
        setCustomerResults([]);
        setShowDropdown(false);
        return;
      }

      customerLookupMutation.mutate(term, {
        onSuccess: (data) => {
          setCustomerResults(data ?? []);
          setSelectedIndex(0);
        },
        onError: () => {
          setCustomerResults([]);
          setShowDropdown(false);
        },
      });
      setShowDropdown(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, selectedCustomer]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const customerInput = target.closest("[data-customer-input]");
        const customerDropdown = target.closest("[data-customer-dropdown]");

        if (!customerInput && !customerDropdown && showDropdown) {
          setCustomerResults([]);
          setShowDropdown(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [showDropdown]);

    // Scroll selected item into view
    useEffect(() => {
      if (selectedItemRef.current) {
        selectedItemRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [selectedIndex]);

    // Keyboard navigation
    useEffect(() => {
      if (!showDropdown || customerResults.length === 0) {
        return;
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < customerResults.length - 1 ? prev + 1 : prev
          );
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          const customer = customerResults[selectedIndex];
          if (customer?.isCustomerToAccount) {
            handleSelect(customer);
          }
        } else if (e.key === "Escape") {
          setShowDropdown(false);
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDropdown, customerResults, selectedIndex]);

    const handleSelect = (customer: CustomerSearchItem) => {
      justSelectedRef.current = true;
      onSelect(customer);
      setSearchText(customer.name);
      setCustomerResults([]);
      setShowDropdown(false);
      setSelectedIndex(0);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    };

    const handleClearClick = () => {
      setSearchText("");
      setCustomerResults([]);
      setShowDropdown(false);
      setSelectedIndex(0);
      if (onClear) {
        onClear();
      }
      // Focus back to the input after clearing
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow backspace or delete to clear when readonly
      if (
        selectedCustomer &&
        !disabled &&
        (e.key === "Backspace" || e.key === "Delete")
      ) {
        e.preventDefault();
        handleClearClick();
      }
    };

    // Effect to sync search text with selectedCustomer
    useEffect(() => {
      if (selectedCustomer && selectedCustomer.name) {
        // Update search text when customer is selected
        setSearchText(selectedCustomer.name);
      } else if (!selectedCustomer) {
        // Clear search text when customer is cleared
        setSearchText("");
      }
    }, [selectedCustomer]);

    return (
      <div className="relative">
        <div data-customer-input>
          <Input
            ref={inputRef}
            label="Customer"
            placeholder="Type customer name to search..."
            value={searchText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            isAsterisk
            autoComplete="off"
            disabled={disabled}
            readOnly={!!selectedCustomer && !disabled}
            onClear={
              selectedCustomer && !disabled ? handleClearClick : undefined
            }
            className={
              selectedCustomer && !disabled ? "cursor-default bg-neutral-5" : ""
            }
            iconRight={
              customerLookupMutation.isLoading && searchText.trim().length >= 2
                ? Icon.Loading
                : undefined
            }
            iconClassName={
              customerLookupMutation.isLoading ? "animate-spin" : undefined
            }
          />
        </div>

        {showDropdown && searchText.trim().length >= 2 && (
          <div
            ref={dropdownRef}
            data-customer-dropdown
            className="absolute z-20 mt-1 w-full bg-white border border-neutral-20 rounded shadow-lg max-h-60 overflow-y-auto"
          >
            {customerLookupMutation.isLoading ? (
              <div className="px-3 py-2 text-center text-neutral-60">
                Loading...
              </div>
            ) : customerResults.length === 0 ? (
              <div className="px-3 py-2 text-center text-neutral-60">
                No customers found
              </div>
            ) : (
              <div>
                {customerResults.map((customer, index) => (
                  <div
                    key={customer.id}
                    ref={index === selectedIndex ? selectedItemRef : null}
                    onClick={() =>
                      customer.isCustomerToAccount && handleSelect(customer)
                    }
                    className={`flex items-center justify-between px-3 py-2 ${
                      customer.isCustomerToAccount
                        ? "cursor-pointer"
                        : "cursor-default"
                    } ${
                      index !== selectedIndex &&
                      "border-transparent hover:bg-neutral-10"
                    }`}
                    style={{
                      backgroundColor:
                        index === selectedIndex ? "#e6f2ff" : undefined,
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <span className="text-neutral truncate">
                          {customer.name}
                        </span>
                        {customer.code && (
                          <span className="text-neutral-60 ml-1 truncate">
                            ({customer.code})
                          </span>
                        )}
                        {customer.isVerified && (
                          <span
                            className="ml-2 flex-shrink-0"
                            title="TOM Customer - This customer is managed by its users on TOM"
                          >
                            <Icon.CheckedCircle
                              size="14"
                              className="fill-blue-dark"
                            />
                          </span>
                        )}
                      </div>
                    </div>
                    {!customer.isCustomerToAccount && (
                      <div className="ml-2 flex-shrink-0">
                        <a
                          href={`/relationship/accounts/connectcustomer/${customer.id}?ret=/sales/documents/orders/placeorder`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button size="sm" color="blue">
                            Connect
                          </Button>
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

export default CustomerLookup;
